import type {
  ConflictMapping,
  DeactivateResult,
  MergeConflict,
  MergeResult,
  NeedsAttentionBreakdown,
  OrgActivity,
  OrgActivityActor,
  OrgActivityPage,
  OrgClient,
  OrgDescendant,
  OrgGraph,
  OrgRecord,
  OrgRoot,
  OrgRootAggregates,
  OrgSourceMapping,
  OrgSummary,
  PaginatedClientsList,
  RenameResult,
  UnlinkedMapping,
  UnlinkResult,
} from '@@/types/Organizations';
import type {
  ConflictMappingData,
  DeactivateResultData,
  MergeConflictData,
  MergeResultData,
  NeedsAttentionBreakdownData,
  OrgActivitiesResponseData,
  OrgActivityActorData,
  OrgActivityData,
  OrgClientData,
  OrgClientsResponseData,
  OrgDescendantData,
  OrgGraphData,
  OrgRecordData,
  OrgRootAggregatesData,
  OrgRootData,
  OrgSourceMappingData,
  OrgSourceMappingsResponseData,
  OrgSummaryData,
  RenameResultData,
  UnlinkedMappingData,
  UnlinkResultData,
} from '@@/types/OrganizationsAPI';

// ----- Org Clients -----
export const serializeOrgClient = (data: OrgClientData): OrgClient => {
  return {
    id: data.id,
    authClientId: data.auth_client_id,
    name: data.name,
    type: data.type,
    typeLabel: data.type_label,
    authStatus: data.auth_status,
    migrationStatus: data.migration_status,
    city: data.city,
    state: data.state,
    stateCode: data.state_code,
    country: data.country,
    countryCode: data.country_code,
    needsAttention: data.needs_attention_count ?? null,
    needsAttentionBreakdown:
      data.needs_attention_count == null ? null : serializeNeedsAttentionBreakdown(data.needs_attention_breakdown),
    canonicalNodeCount: data.canonical_node_count,
  };
};

export const serializeOrgClients = (clients: OrgClientData[]): OrgClient[] => {
  return clients.map(serializeOrgClient);
};

function serializeNeedsAttentionBreakdown(
  data: NeedsAttentionBreakdownData | null | undefined,
): NeedsAttentionBreakdown | null {
  if (data == null) return null;

  return {
    ambiguousMatch: data.ambiguous_match,
    mappingConflict: data.mapping_conflict,
    renameMergeProposed: data.rename_merge_proposed,
    orphanCandidate: data.orphan_candidate,
    other: data.other,
  };
}

export const serializeOrgClientsList = (data: OrgClientsResponseData): PaginatedClientsList => {
  return {
    clients: serializeOrgClients(data.clients),
    page: data.page,
    pages: data.pages,
    limit: data.limit,
    totalCount: data.total_count,
  };
};

// ----- Org Graph -----
export const serializeOrgRoot = (data: OrgRootData): OrgRoot => {
  return {
    id: data.id,
    rootId: data.root_organization_id,
    parentId: data.parent_id,
    path: data.path,
    orgType: data.org_type,
    name: data.name,
    status: data.status,
    version: data.version,
    mergedInto: data.merged_into,
  };
};

export const serializeOrgRootAggregates = (data: OrgRootAggregatesData): OrgRootAggregates => {
  return {
    sourceMappingCount: data.source_mapping_count,
    sourceSystems: data.source_systems,
    needsAttention: data.needs_attention,
  };
};

export const serializeOrgSummary = (data: OrgSummaryData): OrgSummary => {
  return {
    activeCount: data.active_count,
    inactiveCount: data.inactive_count,
    totalCanonicalNodes: data.total_canonical_nodes,
    visibleCount: data.visible_count,
    hiddenCount: data.hidden_count,
  };
};

export const serializeOrgDescendant = (data: OrgDescendantData): OrgDescendant => {
  return {
    id: data.id,
    rootId: data.root_organization_id,
    parentId: data.parent_id,
    path: data.path,
    depth: data.depth,
    orgType: data.org_type,
    name: data.name,
    status: data.status,
    version: data.version,
    sourceMappingCount: data.source_mapping_count,
    sourceSystems: data.source_systems,
    hasChildren: data.has_children,
    childrenCount: data.children_count,
    needsAttention: data.needs_attention,
  };
};

export const serializeOrgDescendants = (descendants: OrgDescendantData[]): OrgDescendant[] => {
  return descendants.map(serializeOrgDescendant);
};

export const serializeOrgGraph = (data: OrgGraphData): OrgGraph => {
  return {
    root: serializeOrgRoot(data.root),
    rootAggregates: serializeOrgRootAggregates(data.root_aggregates),
    summary: serializeOrgSummary(data.summary),
    descendants: serializeOrgDescendants(data.descendants),
    nextCursor: data.next_cursor,
    hasMore: data.has_more,
  };
};

// ----- Source mappings -----
// `unlinked_at` is dropped: the endpoint returns live mappings only, so it is always null.
export const serializeOrgSourceMapping = (data: OrgSourceMappingData): OrgSourceMapping => {
  return {
    id: data.id,
    organizationId: data.organization_id,
    sourceSystem: data.source_system,
    sourceEntityType: data.source_entity_type,
    sourceEntityId: data.source_entity_id,
    sourceDisplayName: data.source_display_name,
    method: data.method,
    matchRule: data.match_rule,
    confidence: data.confidence,
    reviewState: data.review_state,
  };
};

export const serializeOrgSourceMappings = (data: OrgSourceMappingsResponseData): OrgSourceMapping[] => {
  return data.source_mappings.map(serializeOrgSourceMapping);
};

// ----- Admin writes -----
export const serializeOrgRecord = (data: OrgRecordData): OrgRecord => {
  return {
    id: data.id,
    rootId: data.root_organization_id,
    parentId: data.parent_id,
    path: data.path,
    orgType: data.org_type,
    name: data.name,
    status: data.status,
    version: data.version,
    mergedInto: data.merged_into,
  };
};

export const serializeRenameResult = (data: RenameResultData): RenameResult => {
  return { organization: serializeOrgRecord(data.canonical_org) };
};

export const serializeDeactivateResult = (data: DeactivateResultData): DeactivateResult => {
  return {
    organization: serializeOrgRecord(data.canonical_org),
    deactivatedAt: data.deactivated_at,
    affectedCount: data.affected_org_count,
  };
};

export const serializeMergeResult = (data: MergeResultData): MergeResult => {
  return {
    merged: true,
    loser: serializeOrgRecord(data.loser),
    survivor: serializeOrgRecord(data.survivor),
    movedMappingCount: data.moved_mapping_count,
    movedDescendantCount: data.moved_descendant_count,
  };
};

const serializeConflictMapping = (data: ConflictMappingData): ConflictMapping => {
  return {
    id: data.id,
    sourceEntityType: data.source_entity_type,
    sourceEntityId: data.source_entity_id,
    sourceDisplayName: data.source_display_name,
  };
};

export const serializeMergeConflict = (data: MergeConflictData): MergeConflict => {
  return {
    sourceSystem: data.source_system,
    sourceEntityType: data.source_entity_type,
    loserMapping: serializeConflictMapping(data.loser_mapping),
    survivorMapping: serializeConflictMapping(data.survivor_mapping),
  };
};

// `unlinked_at` is dropped like the live-mapping read drops it: the card is gone once unlinked.
const serializeUnlinkedMapping = (data: UnlinkedMappingData): UnlinkedMapping => {
  return {
    id: data.id,
    organizationId: data.organization_id,
    sourceSystem: data.source_system,
    sourceEntityType: data.source_entity_type,
    sourceEntityId: data.source_entity_id,
    sourceDisplayName: data.source_display_name,
    method: data.method,
    matchRule: data.match_rule,
    confidence: data.confidence,
  };
};

export const serializeUnlinkResult = (data: UnlinkResultData): UnlinkResult => {
  return {
    mapping: data.mapping ? serializeUnlinkedMapping(data.mapping) : null,
    remainingLiveMappings: data.remaining_live_mappings,
    orphanCandidate: data.orphan_candidate,
  };
};

// ----- Node activity -----

/*
 * Each kind keeps only the field that identifies it. Fields are picked by name rather than spread,
 * so a field the service adds later — an email, say — never reaches the UI by accident.
 */
const serializeOrgActivityActor = (data: OrgActivityActorData): OrgActivityActor => {
  switch (data.kind) {
    case 'operator':
      return { kind: 'operator', id: data.id ?? '', name: data.name ?? null };
    case 'product':
      return { kind: 'product', sourceSystem: data.source_system ?? '' };
    case 'import':
      return { kind: 'import', importRunId: data.import_run_id ?? null };
    case 'provisioning':
    case 'system':
    case 'unrecorded':
      return { kind: data.kind };
    default:
      return { kind: 'other', rawKind: data.kind };
  }
};

export const serializeOrgActivity = (data: OrgActivityData): OrgActivity => {
  return {
    id: data.id,
    type: data.type,
    occurredAt: new Date(data.occurred_at),
    actor: serializeOrgActivityActor(data.actor),
    reason: data.reason,
    cascadeFrom: data.cascade_from,
    details: data.details,
  };
};

export const serializeOrgActivityPage = (data: OrgActivitiesResponseData): OrgActivityPage => {
  return {
    activities: data.activities.map(serializeOrgActivity),
    nextCursor: data.next_cursor,
    hasMore: data.has_more,
  };
};
